import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaBuilding, FaHandshake, FaPlus } from 'react-icons/fa';
import { partnerIncomeService } from '../services/partnerIncomeService';
import { projectService, Project } from '../services/projectService';
import { usePageTitle } from '../hooks/usePageTitle';
import '../css/pages/AddPartnerIncome.css';

interface Partner {
  id: number;
  name: string;
  percentage: number;
}

const AddPartnerIncome: React.FC = () => {
  usePageTitle();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProjectName, setSelectedProjectName] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  
  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    receiptName: '',
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'cash' as const,
    referenceNumber: '',
    bankName: '',
    remarks: '',
    transactionType: 'income' as const
  });

  useEffect(() => {
    loadProjects();
    
    // Check if there's a project ID in URL params
    const projectId = searchParams.get('projectId');
    if (projectId) {
      loadProjectById(parseInt(projectId));
    }

    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.searchable-dropdown')) {
        setShowProjectDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [searchParams]);

  useEffect(() => {
    if (selectedProject) {
      loadPartnersByProject(parseInt(selectedProject));
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);
    } catch (err) {
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectById = async (projectId: number) => {
    try {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        setSelectedProject(project.id.toString());
        setSelectedProjectName(project.name);
        setProjectSearchTerm(project.name);
      }
    } catch (err) {
      setError('Failed to load project');
    }
  };

  const loadPartnersByProject = async (projectId: number) => {
    try {
      setLoading(true);
      const partnersData = await partnerIncomeService.getPartnersByProject(projectId);
      setPartners(partnersData);
    } catch (err) {
      setError('Failed to load partners for this project');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProject || !selectedPartner) return;
    
    try {
      setLoading(true);
      await partnerIncomeService.createPartnerIncome({
        partnerId: selectedPartner.id,
        projectId: parseInt(selectedProject),
        amount: parseFloat(paymentForm.amount),
        paymentDate: paymentForm.paymentDate,
        paymentMethod: paymentForm.paymentMethod,
        referenceNumber: paymentForm.referenceNumber || undefined,
        bankName: paymentForm.bankName || undefined,
        remarks: paymentForm.remarks || undefined,
        receiptName: paymentForm.receiptName || undefined,
        transactionType: paymentForm.transactionType
      });

      // Reset form and navigate back
      setPaymentForm({
        receiptName: '',
        amount: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'cash',
        referenceNumber: '',
        bankName: '',
        remarks: '',
        transactionType: 'income'
      });

      navigate('/ledger/partner');
    } catch (err) {
      setError('Failed to add partner transaction');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project.id.toString());
    setSelectedProjectName(project.name);
    setProjectSearchTerm(project.name);
    setShowProjectDropdown(false);
    setSelectedPartner(null); // Reset partner when project changes
  };

  const handlePartnerSelect = (partner: Partner) => {
    setSelectedPartner(partner);
    setShowPaymentForm(true);
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(projectSearchTerm.toLowerCase())
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{ color: '#1a1a1a' }}>
          <h1 style={{ color: '#1a1a1a', margin: '0 0 4px 0' }}>Add Partner Transaction</h1>
        </div>
        <button className="back-btn" onClick={() => navigate('/ledger/partner')}>
          <FaArrowLeft /> Back
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Step 1: Select Project */}
      <div className="step-section">
        <div className="step-header">
          <FaBuilding className="step-icon" />
          <h2>Step 1: Select Project</h2>
        </div>
        
        <div className="form-group">
          <label>Project *</label>
          <div className="searchable-dropdown">
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Search projects..."
                value={projectSearchTerm}
                onChange={(e) => {
                  setProjectSearchTerm(e.target.value);
                  setShowProjectDropdown(true);
                }}
                onFocus={() => setShowProjectDropdown(true)}
                className="form-input"
              />
            </div>
            {showProjectDropdown && filteredProjects.length > 0 && (
              <div className="dropdown-options">
                {filteredProjects.map(project => (
                  <div
                    key={project.id}
                    className="dropdown-option"
                    onClick={() => handleProjectSelect(project)}
                  >
                    <div className="project-option">
                      <div className="project-name">{project.name}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Step 2: Select Partner */}
      {selectedProject && (
        <div className="step-section">
          <div className="step-header">
            <FaHandshake className="step-icon" />
            <h2>Step 2: Select Partner</h2>
          </div>
          
          {loading ? (
            <div className="loading">Loading partners...</div>
          ) : partners.length > 0 ? (
            <div className="partners-grid">
              {partners.map(partner => (
                <div
                  key={partner.id}
                  className={`partner-card ${selectedPartner?.id === partner.id ? 'selected' : ''}`}
                  onClick={() => handlePartnerSelect(partner)}
                >
                  <div className="partner-info">
                    <h3>{partner.name}</h3>
                    <p>Share: {partner.percentage}%</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-partners">
              <p>No partners found for this project</p>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Payment Form */}
      {showPaymentForm && selectedPartner && (
        <div className="step-section">
          <div className="step-header">
            <FaPlus className="step-icon" />
            <h2>Step 3: Transaction Details</h2>
          </div>

          <div className="selected-info">
            <p><strong>Project:</strong> {selectedProjectName}</p>
            <p><strong>Partner:</strong> {selectedPartner.name}</p>
          </div>

          <form onSubmit={handleSubmit} className="payment-form">
            <div className="form-row">
              <div className="form-group">
                <label>Receipt Name</label>
                <input
                  type="text"
                  value={paymentForm.receiptName}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, receiptName: e.target.value }))}
                  placeholder="Name of person giving/receiving payment"
                />
              </div>
              
              <div className="form-group">
                <label>Transaction Type *</label>
                <select
                  value={paymentForm.transactionType}
                  onChange={(e) => setPaymentForm(prev => ({ 
                    ...prev, 
                    transactionType: e.target.value as any 
                  }))}
                  required
                >
                  <option value="income">Income (Received from Partner)</option>
                  <option value="disbursement">Disbursement (Paid to Partner)</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                  onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                  required
                  placeholder="Enter amount"
                  step="0.01"
                />
              </div>
              
              <div className="form-group">
                <label>Payment Date *</label>
                <input
                  type="date"
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentDate: e.target.value }))}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Payment Method *</label>
                <select
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm(prev => ({ 
                    ...prev, 
                    paymentMethod: e.target.value as any 
                  }))}
                  required
                >
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="neft">NEFT</option>
                  <option value="imps">IMPS</option>
                  <option value="rtgs">RTGS</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Reference Number</label>
                <input
                  type="text"
                  value={paymentForm.referenceNumber}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, referenceNumber: e.target.value }))}
                  placeholder="Cheque number, transaction ID, etc."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Bank Name</label>
                <input
                  type="text"
                  value={paymentForm.bankName}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, bankName: e.target.value }))}
                  placeholder="Bank name (for electronic transfers)"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                value={paymentForm.remarks}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, remarks: e.target.value }))}
                placeholder="Additional notes or remarks"
                rows={3}
              />
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => navigate('/ledger/partner')}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-btn"
                disabled={loading}
              >
                {loading ? 'Adding...' : `Add ${paymentForm.transactionType === 'income' ? 'Income' : 'Disbursement'}`}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AddPartnerIncome;
